import { DestroyRef, Injectable } from '@angular/core'
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http'
import { environment } from '../../environments/environment'
import { MsalService } from '@azure/msal-angular'
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser'
import { BehaviorSubject, catchError, firstValueFrom, Observable, tap } from 'rxjs'
import { PlatformService } from './platform.service'
import { Menu } from '../interfaces/api/Menu'
import { UserRole } from '../interfaces/api/UserRole'
import { AccountType } from '../interfaces/api/AccountType'
import { Gallery } from '../interfaces/api/Gallery'
import { LabelValuePair } from '../interfaces/api/LabelValuePair'
import { CAcademyFormation } from '../interfaces/c-academy-formation'
import { Feedback } from 'app/interfaces/feedback.interface'
import { Claim, ClaimComments, ClaimDocument, ClaimWarrantyInfo } from 'app/interfaces/claim.interface'
import { ClaimListResponse } from '@components/my-claims/claim-list/claim-list.component'
import { ClaimInformation } from '../interfaces/claim-information'
import { PagedInvoiceList } from '../interfaces/invoice-claim.interface'
import { CatalogueColorDTO } from 'app/interfaces/api/CatalogueColorDTO'
import { DraftSaleApiResponse } from '../interfaces/draft-response.interface'
import { PartnerStore, RelatedFabricator } from '../interfaces/partner-store'
import { PartnerAccount } from 'app/interfaces/partner-account.interface'
import { ContractType, SignableContract } from '../interfaces/contract-type'
import { ChildAccountInfo } from '../interfaces/child-account-info.interface'
import { AccountSocialInfo } from 'app/interfaces/account-social-info.interface'
import { AccountUpdateRequest } from 'app/interfaces/account-update-request.interface'
import { AccountSocialUpdateRequest } from 'app/interfaces/account-social-update-request.interface'
import { PicklistValueDTO } from 'app/interfaces/picklist-value.interface'
import { ActivationResult, AssociateShopDto, EmployeeDto, EmployeeUpdateRequest } from 'app/interfaces/employee.interface'
import { SaveSalesDraftBody, UpdateSaleBody, SaleItemInput } from 'app/interfaces/sales-body.interface'
import { Router } from '@angular/router'
import { KitchenColor } from '../interfaces/kitchen-color'
import { jwtDecode } from 'jwt-decode'
import { OnlineVisitCooldownDTO, OnlineVisitRequestDTO, OnlineVisitResponseDTO } from 'app/interfaces/online-visit.interface'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { NaturalStoneColor } from '../interfaces/natural-stone/natural-stone-color'
import { NaturalStoneLotParsed, NaturalStoneLots } from '../interfaces/natural-stone/natural-stone-lots'
import { getMigratedUrl } from './utils'

export type UserInfo = {
    id: string | null
    accountId: string | null
    accountName: string | null
    accountType: AccountType | null
    accountStreet?: string | null
    accountPostalCode?: string | null
    accountCity?: string | null
    accountState?: string | null
    accountCountry?: string | null
    accountPhone?: string | null
    accountEmail?: string | null
    accountOwnerId?: string | null
    accountOwnerEmail?: string | null
    accountOwnerFullName?: string | null
    accountOwnerPhone?: string | null
    impersonatedBy: string | null
    firstName: string | null
    lastName: string | null
    email: string | null
    phone: string | null
    country: string | null
    market: string | null
    language: string | null
    locale: string | null
    currency: string | null
    lengthUnit: string | null
    thicknessUnit: string | null
    surfaceAreaUnit: string | null

    sapCustomerId: string | null
    sapSalesOrgId: string | null
    sapCenterId: string | null

    salesforceAccountId: string | null
    salesforceContactId: string | null

    centerName: string | null
    centerFullAddress: string | null

    role: UserRole | null
    authorities: string[]
    loyaltyLevel: string | null
    silestonePresence: number | null
    dektonPresence: number | null
    ytdSales: number | null
    cosentinoPresence: number | null

    fiscalId: string | null
    sapIdSupplier: string | null
    supplierStatus: string | null

    allowImpersonateAccess?: boolean
    hasChildAccounts?: boolean
    hasEcosentinoAccess?: boolean
    hasFinancialPortalAccess?: boolean
    hasWeAccess?: boolean
    hasNexusAccess?: boolean
    hasNotificationsFromLeads?: boolean
    position?: string | null
    isActiveLeadsB2C?: boolean
    isActiveNexusProgram?: boolean
    isTemporallyDisabledOnLeads?: boolean
    pointsExchangeMethodByEmployee?: boolean

    mainMenu: Menu
    otherServicesMenu: Menu
}

const DEFAULT_USER_INFO: UserInfo = {
    id: null,
    accountId: null,
    accountName: null,
    accountType: null,
    impersonatedBy: null,
    firstName: null,
    lastName: null,
    email: null,
    phone: null,
    country: null,
    market: null,
    language: null,
    locale: null,
    currency: null,
    lengthUnit: null,
    thicknessUnit: null,
    surfaceAreaUnit: null,

    sapCustomerId: null,
    sapSalesOrgId: null,
    sapCenterId: null,

    salesforceAccountId: null,
    salesforceContactId: null,

    centerName: null,
    centerFullAddress: null,

    role: null,
    authorities: [],
    loyaltyLevel: null,
    silestonePresence: null,
    dektonPresence: null,
    ytdSales: null,
    cosentinoPresence: null,
    fiscalId: null,
    sapIdSupplier: null,
    supplierStatus: null,

    mainMenu: { items: [] },
    otherServicesMenu: { items: [] },
}

@Injectable({
    providedIn: 'root',
})
export class ApiService {
    public initialUrl: URL;
    public redirectUrl?: string;
    public redirectSavedInSessionStorage?: string
    public isLoggedIn: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
    public userInfo: BehaviorSubject<UserInfo> = new BehaviorSubject<UserInfo>(DEFAULT_USER_INFO)

    public hasLoginError: boolean = false

    constructor(private http: HttpClient, private authService: MsalService, private router: Router, private destroyRef: DestroyRef, private platformService: PlatformService) {
        this.initialUrl = new URL(window.location.href)
        this.redirectUrl = getMigratedUrl(this.initialUrl.searchParams.get('redirect') || undefined)
        if(this.redirectUrl){
            sessionStorage.setItem('redirect', this.redirectUrl)
        }
        this.redirectSavedInSessionStorage = sessionStorage.getItem('redirect') || undefined
        if (this.initialUrl.searchParams.has('token')) {
            const authToken = this.initialUrl.searchParams.get('token') || ''
            const codedToken = jwtDecode(authToken)

            const rawData = (codedToken as any).data
            const userData = JSON.parse(rawData) as UserInfo

            this.setUserInfo(authToken, userData)
        }

        this.isLoggedIn.pipe(takeUntilDestroyed(destroyRef)).subscribe({
            next: (isLoggedIn) => {
                if(isLoggedIn){
                    console.log('isLoggedIn is true. checking redirectUrl', this.redirectUrl, this.redirectSavedInSessionStorage)
                    if(this.redirectSavedInSessionStorage){
                        sessionStorage.removeItem('redirect')
                        location.href = this.redirectSavedInSessionStorage
                    }
                }
            }
        })

        this.loadUserInfo()
    }

    loadUserInfo() {
        const userInfoJson = localStorage.getItem('weUserInfo')
        const authToken = localStorage.getItem('weToken')
        if (userInfoJson) {
            try {
                this.isLoggedIn.next(true)
                this.userInfo.next(JSON.parse(userInfoJson))
            } catch (e) {
                console.error('ApiService::loadUserInfo', e)
            }
        } else if(authToken) {
            const codedToken = jwtDecode(authToken)

            const rawData = (codedToken as any).data
            const userData = JSON.parse(rawData) as UserInfo

            this.setUserInfo(authToken, userData)
        }
    }

    setUserInfo(token: string, userInfo: UserInfo) {
        localStorage.setItem('weToken', token)
        localStorage.setItem('weUserInfo', JSON.stringify(userInfo))

        this.isLoggedIn.next(true)
        this.userInfo.next(userInfo)
    }

    updateUserInfo(userInfo: UserInfo): void {
        localStorage.setItem('weUserInfo', JSON.stringify(userInfo))
        this.userInfo.next(userInfo)
    }

    async handleLoginFlow(force = false) {
        console.log('handleLoginFlow start')
        if (environment.webComponent) return
        if (!force) {
            if (this.isLoggedIn.value){
                // Check if userInfo is valid
                if(Object.keys(this.userInfo.getValue()).length < 10){
                    this.http.get<UserInfo>(environment.API_URL + '/auth/user-info').subscribe({
                        next: (userInfo) => {
                            this.updateUserInfo(userInfo)
                            Promise.resolve()
                        },
                        error: (err) => {
                            console.error('ApiService::webComponentLogin', 'Failed to get JWT token')
                            this.navigateToLoginError()
                            Promise.resolve()
                        },
                    })
                }

                return
            }
        }

        await this.authService.instance.initialize()
        console.log('authService.instance.initialize start')
        if(this.initialUrl.hash && this.initialUrl.hash.length > 10 && this.initialUrl.hash.includes('#state=')){
            try{
                const redirectPromise = await this.authService.instance.handleRedirectPromise(this.initialUrl.hash)
                if(redirectPromise && redirectPromise.account){
                    console.log('Login with handleRedirectPromise')
                    this.handleMsalResponse(redirectPromise.account)
                    return;
                } else {
                    console.log('No account found or handleRedirectPromise is null')
                }
            } catch (e){
                console.error('Error with handleRedirectPromise', e, this.initialUrl.hash)
            }
        }

        const accounts = this.authService.instance.getAllAccounts()
        if (accounts.length > 0) {
            const account = accounts[0]
            // Set the active account so MSAL knows which user to target
            this.authService.instance.setActiveAccount(account)

            let tokenRequest: any = {
                scopes: ['openid', 'profile', 'offline_access'],
                account: account,
            }
            if(this.redirectSavedInSessionStorage?.includes(environment.previousDomain)){
                tokenRequest.redirectUri = environment.alternativeRedirectUri
            }

            try {
                // Attempt silent token acquisition with the specified account
                console.log('Attempt silent token acquisition with the specified account')
                const result = await this.authService.instance.acquireTokenSilent(tokenRequest)
                console.log('silent token acquisition success')
                this.handleMsalResponse(result.account)
            } catch (error) {
                console.error('ApiService::handleLoginFlow', error)
                // Abort if B2C reports unsupported scope
                if ((error as any).errorCode === 'invalid_request') {
                    console.error('Unsupported scope in B2C flow, aborting redirect', error)
                    return
                }
                if (error instanceof InteractionRequiredAuthError) {
                    this.logout()
                    // Fallback to redirect if silent acquisition fails due to interaction requirement
                    return this.authService.instance.acquireTokenRedirect(tokenRequest)
                }
                this.navigateToLoginError()
            }
        } else {
            // No accounts, initiate interactive login
            console.log('No accounts, initiate interactive login')
            if(!this.redirectUrl &&
                this.initialUrl.pathname &&
                this.initialUrl.pathname !== '/' &&
                !this.initialUrl.pathname.includes('login') &&
                !this.initialUrl.pathname.includes('home'))
            {
                this.redirectUrl = this.initialUrl.toString();
                sessionStorage.setItem('redirect', this.redirectUrl)
                this.redirectSavedInSessionStorage = sessionStorage.getItem('redirect') || undefined
            }

            return this.redirectSavedInSessionStorage?.includes(environment.previousDomain) ?
                this.authService.instance.loginRedirect({
                    scopes: ['openid', 'profile', 'offline_access'],
                    redirectUri: environment.alternativeRedirectUri
                }) :
                this.authService.instance.loginRedirect({
                    scopes: ['openid', 'profile', 'offline_access'],
                })
        }
    }

    handleMsalResponse(account: AccountInfo) {
        const adToken = account.idToken
        const email = account.name
        if (email && adToken) {
            this.getJWTToken(adToken).subscribe({
                next: (response) => {
                    this.setUserInfo(response.token, response.userInfo)
                },
                error: (e) => {
                    console.error('ApiService::handleMsalResponse', 'Failed to get JWT token', e)
                    this.navigateToLoginError()
                },
            })
        }
    }

    webComponentLogin() {
        const authToken = localStorage.getItem('weToken')

        if (!authToken) {
            console.error('ApiService::webComponentLogin - No auth token found')
            this.navigateToLoginError()
            return
        }

        this.http.get<UserInfo>(environment.API_URL + '/auth/user-info').subscribe({
            next: (userInfo) => {
                this.setUserInfo(authToken, userInfo)
            },
            error: (err) => {
                console.error('ApiService::webComponentLogin', 'Failed to get JWT token')
                this.navigateToLoginError()
            },
        })
    }

    getJWTToken(adToken: string) {
        const url = environment.API_URL + '/auth/login'
        const body = new URLSearchParams()
        body.set('adToken', adToken)

        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
        })

        return this.http.post<{ token: string; userInfo: UserInfo }>(url, body.toString(), { headers })
    }

    navigateToLoginError() {
        this.hasLoginError = true
        this.isLoggedIn.next(false)
        this.userInfo.next(DEFAULT_USER_INFO)
        this.router.navigate(['/login-error'])
    }

    logout(redirect: string | undefined = undefined) {
        // localStorage.removeItem('token')
        // localStorage.removeItem('weUserInfo')
        localStorage.clear() //REVIEW:: better way to clear only app-related data?
        sessionStorage.clear()
        if(environment.webComponent){
            this.authService.instance.initialize().then(async () => {
                await firstValueFrom(this.authService.logout())
                window.location.href = environment.frontEndUrl
            }).catch(error => {
                console.log("Error initializing login for logout inside webcomponent", error)
                window.location.href = environment.frontEndUrl
            }).finally(() => {
                window.location.href = environment.frontEndUrl
            })
        } else {
            try{
                let redirectObj: {postLogoutRedirectUri: string} | undefined;
                if(redirect){
                    redirectObj = {postLogoutRedirectUri: redirect};
                }
                this.authService.logoutRedirect(redirectObj)
            } catch (exception){
                console.error("Error logging out", exception)
            }
        }
    }

    fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()

            reader.onload = () => {
                try {
                    const base64String = reader.result as string
                    const base64 = base64String.split(',')[1]
                    resolve(base64)
                } catch (error) {
                    reject(error)
                }
            }

            reader.onerror = (error) => {
                reject(error)
            }

            reader.readAsDataURL(file)
        })
    }

    isUsaUser(){
        return !!(this.userInfo.value.country && (this.userInfo.value.country.toUpperCase() === 'US' || this.userInfo.value.country.toUpperCase() === 'CA'))
    }

    // -----------------

    myFeedback__create(feedback: Feedback) {
        return this.http.post<Feedback>(environment.API_URL + '/feedback', feedback)
    }

    myGalleries__list(query: string) {
        const params = new HttpParams().set('query', query)

        return this.http.get<Gallery[]>(environment.API_URL + '/gallery', { params })
    }

    getSamplesList(filteredParams?: any) {
        let queryString = ''
        if (filteredParams) {
            let query = new URLSearchParams(filteredParams)
            queryString = query.toString()
        }

        return this.http.get<any>(environment.API_URL + '/samples?' + queryString)
    }

    getSampleDetail(id: string) {
        return this.http.get<any>(environment.API_URL + '/samples/' + id)
    }
    getClaimList(filteredParams?: any) {
        let queryString = ''
        if (filteredParams) {
            let query = new URLSearchParams(filteredParams)
            queryString = query.toString()
        }

        return this.http.get<ClaimListResponse>(environment.API_URL + '/claims?' + queryString)
    }

    getClaim(caseId: string) {
        return this.http.get<Claim>(environment.API_URL + '/claims/' + caseId)
    }
    getDraftSale(id: string) {
        return this.http.get<DraftSaleApiResponse>(environment.API_URL + '/sales/draftsale/' + id)
    }

    getClaimComments(caseId: string) {
        return this.http.get<ClaimComments[]>(environment.API_URL + '/claims/' + caseId + '/comments')
    }
    sendClaimComment(caseId: string, comment: string) {
        return this.http.post<any>(environment.API_URL + '/claims/' + caseId + '/comments', comment)
    }
    updateClaimNotes(caseId: string, notes: string) {
        return this.http.post<any>(environment.API_URL + '/claims/' + caseId + '/notes', notes)
    }
    getClaimDocuments(caseId: string) {
        return this.http.get<ClaimDocument[]>(environment.API_URL + '/claims/' + caseId + '/documents')
    }
    downloadClaimDocument(caseId: string, documentId: string): Observable<Blob> {
        return this.http.get(`${environment.API_URL}/claims/${caseId}/documents/${documentId}`, {
            responseType: 'blob' as 'blob',
        })
    }
    downloadAllClaimDocuments(caseId: string): Observable<Blob> {
        return this.http.get(`${environment.API_URL}/claims/${caseId}/documents/download-all`, {
            responseType: 'blob' as 'blob',
        })
    }
    deleteClaim(caseId: string) {
        return this.http.delete<Claim>(environment.API_URL + '/claims/' + caseId)
    }

    myGalleries__view(id: string) {
        return this.http.get<Gallery>(environment.API_URL + '/gallery/' + id)
    }

    myGalleries__create(gallery: Gallery) {
        return this.http.post<Gallery>(environment.API_URL + '/gallery', gallery)
    }

    myGalleries__categories() {
        return this.http.get<string[]>(environment.API_URL + '/gallery/categories')
    }

    myGalleries__spaces() {
        return this.http.get<string[]>(environment.API_URL + '/gallery/spaces')
    }

    myGalleries__styles() {
        return this.http.get<string[]>(environment.API_URL + '/gallery/styles')
    }

    myGalleries__collaborators(isSales: boolean = false, accountId?: string | null) {
        return this.http.get<LabelValuePair<string>[]>(
            environment.API_URL + '/gallery/collaborators?' + (isSales ? 'sales=true&' : '') + (accountId ? 'accountId=' + accountId : '&')
        )
    }

    myGalleries__applications() {
        return this.http.get<string[]>(environment.API_URL + '/gallery/applications')
    }

    myGalleries__brands() {
        return this.http.get<string[]>(environment.API_URL + '/gallery/brands', { params: this.addPlatformParam() })
    }

    myGalleries__colors() {
        let params = new HttpParams()
        return this.http.get<LabelValuePair<string>[]>(environment.API_URL + '/gallery/colors', { params: this.addPlatformParam(params) })
    }
    myGalleries__colorsByBrand(brand?: string | null) {
        let params = new HttpParams().set('nameBrand', encodeURIComponent(brand?.trim() || ''))
        params = this.addPlatformParam(params)
        const url = environment.API_URL + '/gallery/colors/brand';
        return this.http.get<LabelValuePair<string>[]>(url, { params });
    }
    getNextLoyaltyLevelRequirements(loyalty: string, countryCode: string) {
        return this.http.get<any>(environment.API_URL + '/loyalty/next-level?loyalty=' + loyalty + '&countryCode=' + countryCode)
    }

    getCurrentLoyaltyLevelRequirements(loyalty: string, countryCode: string) {
        return this.http.get<any>(environment.API_URL + '/loyalty/current-level?loyalty=' + loyalty + '&countryCode=' + countryCode)
    }
    getLoyaltyPoints() {
        return this.http.get<any>(environment.API_URL + '/loyalty/points-summary')
    }
    getPointsTransactions(page: number = 0, size: number = 10) {
        return this.http.get<any>(environment.API_URL + '/loyalty/transactions?page=' + page + '&pageSize=' + size)
    }
    getPointsEmployeeSummary(page: number = 0, size: number = 10) {
        return this.http.get<any>(environment.API_URL + '/loyalty/employee-summary?page=' + page + '&pageSize=' + size)
    }
    getPointsRedeemed(page: number = 0, size: number = 10) {
        return this.http.get<any>(environment.API_URL + '/loyalty/rewards?page=' + page + '&pageSize=' + size)
    }

    getAvailableFormations() {
        return this.http.get<CAcademyFormation[]>(environment.API_URL + '/c-academy/available-formations')
    }

    getFormationById(id: string) {
        return this.http.get<{ formation: CAcademyFormation; relatedFormations: CAcademyFormation[] }>(environment.API_URL + '/c-academy/formation/' + id)
    }

    toggleFormationFavourite(formationId: string) {
        return this.http.patch<boolean>(environment.API_URL + '/c-academy/formation/' + formationId + '/toggle-favourite', {})
    }

    setFormationRating(formationId: string, rating: number) {
        return this.http.patch<boolean>(environment.API_URL + '/c-academy/formation/' + formationId + '/rating', { rating })
    }

    setFormationStarted(formationId: string) {
        return this.http.patch<boolean>(environment.API_URL + '/c-academy/formation/' + formationId + '/start', {})
    }

    setFormationCompleted(formationId: string) {
        return this.http.patch<boolean>(environment.API_URL + '/c-academy/formation/' + formationId + '/complete', {})
    }

    getTeamMembers(accountId: string | null): Observable<any[]> {
        return this.http.get<any[]>(`${environment.API_URL}/sales/team-members` + (accountId ? '?accountId=' + accountId : ''))
    }
    getClaimDraft(claimId: string) {
        return this.http.get<ClaimInformation>(environment.API_URL + '/claims/draft/' + claimId)
    }

    saveClaimDraft(newClaim: ClaimInformation) {
        // Clean invoice dates that can cause parsing errors on backend
        const claimToSend = this.cleanClaimInvoiceDates(newClaim)
        return this.http.post<ClaimInformation>(environment.API_URL + '/claims/draft', claimToSend)
    }

    private cleanClaimInvoiceDates(claim: ClaimInformation): ClaimInformation {
        // Create a deep copy to avoid modifying the original
        const claimCopy = JSON.parse(JSON.stringify(claim))

        // Function to recursively clean ISO date strings from objects
        const cleanDates = (obj: any): void => {
            if (obj && typeof obj === 'object') {
                for (const key in obj) {
                    if (obj.hasOwnProperty(key)) {
                        const value = obj[key]
                        if (typeof value === 'string' && this.isISODateString(value)) {
                            // Remove ISO date strings that can cause parsing errors
                            delete obj[key]
                        } else if (typeof value === 'object' && value !== null) {
                            cleanDates(value)
                        }
                    }
                }
            }
        }

        // Clean all ISO date strings from the claim copy
        cleanDates(claimCopy)

        return claimCopy
    }

    private isISODateString(value: string): boolean {
        // Check if string matches ISO 8601 date format (e.g., "2022-10-24T00:00:00Z" or "2001-01-08T08:38:27Z")
        const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/
        return isoDateRegex.test(value)
    }

    mySales__catalogueItems(): Observable<CatalogueColorDTO[]> {
        return this.http.get<CatalogueColorDTO[]>(`${environment.API_URL}/sales/catalogue-items`, { params: this.addPlatformParam() })
    }

    mySales__relatedAccounts() {
        const params = new HttpParams().set('includeMyself', true)
        return this.http.get<LabelValuePair<string>[]>(environment.API_URL + '/sales/accounts', {params})
    }

    deleteSale(saleId: string) {
        return this.http.delete<boolean>(environment.API_URL + '/sales/' + saleId)
    }

    saveClaim(newClaim: ClaimInformation) {
        // Clean invoice dates that can cause parsing errors on backend
        const claimToSend = this.cleanClaimInvoiceDates(newClaim)
        return this.http.post<boolean>(environment.API_URL + '/claims/save', claimToSend)
    }

    uploadClaimDocument(id: string, file: any, type: string, typeTranslation: string, isFeedComment = false) {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('type', type)
        formData.append('typeTranslation', typeTranslation)
        formData.append('isFeedComment', isFeedComment ? 'true' : 'false')
        return this.http.post<ClaimDocument>(environment.API_URL + '/claims/draft/' + id + '/document', formData)
    }

    deleteClaimDocument(claimId: string, documentId: string) {
        return this.http.delete<boolean>(environment.API_URL + '/claims/draft/' + claimId + '/document/' + documentId)
    }

    getAvailableWarrantiesForDraft(claimId: string) {
        return this.http.get<ClaimWarrantyInfo[] | null>(environment.API_URL + '/claims/draft/' + claimId + '/warranty')
    }

    getClaimInvoices(currentPage: number, pageSize: number, startDate?: string, endDate?: string, accountId?: string, invoiceNumber?: string, lotNumber?: string, colorCode?: string, claimId?: string) {
        let params = new HttpParams().set('page', currentPage).set('size', pageSize)
        if (startDate) params = params.set('startDate', startDate)
        if (endDate) params = params.set('endDate', endDate)
        if (accountId) params = params.set('accountId', accountId)
        if (invoiceNumber) params = params.set('invoiceId', invoiceNumber)
        if (lotNumber) params = params.set('lotId', lotNumber)
        if (colorCode) params = params.set('colorCode', colorCode)
        if (claimId) params = params.set('claimId', claimId)
        params = params.set('type', 'CLAIMS')

        return this.http.get<PagedInvoiceList>(environment.API_URL + '/invoices', { params })
    }

    getSaleInvoices(currentPage: number, pageSize: number, startDate?: string, endDate?: string, accountId?: string, invoiceNumber?: string, lotNumber?: string, colorCode?: string, saleId?: string) {
        let params = new HttpParams().set('page', currentPage).set('size', pageSize)
        if (startDate) params = params.set('startDate', startDate)
        if (endDate) params = params.set('endDate', endDate)
        if (accountId) params = params.set('accountId', accountId)
        if (invoiceNumber) params = params.set('invoiceId', invoiceNumber)
        if (lotNumber) params = params.set('lotId', lotNumber)
        if (colorCode) params = params.set('colorCode', colorCode)
        if (saleId) params = params.set('saleId', saleId)
        params = params.set('type', 'SALES')

        return this.http.get<PagedInvoiceList>(environment.API_URL + '/invoices', { params })
    }

    getAvailableInvoiceColors(startDate?: string, endDate?: string, accountId?: string, invoiceNumber?: string, lotNumber?: string, colorCode?: string) {
        let params = new HttpParams()
        if (startDate) params = params.set('startDate', startDate)
        if (endDate) params = params.set('endDate', endDate)
        if (accountId) params = params.set('accountId', accountId)
        if (invoiceNumber) params = params.set('invoiceId', invoiceNumber)
        if (lotNumber) params = params.set('lotId', lotNumber)
        if (colorCode) params = params.set('colorCode', colorCode)

        return this.http.get<{ id: string; label: string; value: string }[]>(environment.API_URL + '/invoices/colors', { params })
    }

    getRelatedFabricators(claimId: string){
        return this.http.get<RelatedFabricator[]>(environment.API_URL + '/claims/draft/' + claimId + '/related-fabricators')
    }

    getPartnerStores(claimId: string) {
        return this.http.get<PartnerStore[]>(environment.API_URL + '/claims/draft/' + claimId + '/partner-stores')
    }

    saveSalesDraft(body: SaveSalesDraftBody): Observable<any> {
        const url = `${environment.API_URL}/sales/draft`

        const formData = new FormData()

        const salePayload = {
            fabricatorName: body.fabricatorName || '',
            contactId: body.teamMember || '',
            invoiceDate: body.invoiceDate || '',
            invoiceNumber: body.invoiceNumber || undefined,
            saleItems: body.saleItems || [],
            warrantyCustomerAddress: body.warrantyCustomerAddress || '',
            warrantyCustomerCity: body.warrantyCustomerCity || '',
            warrantyCustomerCountry: body.warrantyCustomerCountry || '',
            warrantyCustomerEmail: body.warrantyCustomerEmail || '',
            warrantyCustomerLastName: body.warrantyCustomerLastName || '',
            warrantyCustomerName: body.warrantyCustomerName || '',
            warrantyCustomerPhone: body.warrantyCustomerPhone || '',
            warrantyCustomerState: body.warrantyCustomerState || '',
            warrantyCustomerZIPCode: body.warrantyCustomerZIPCode || '',
            status__c: body.status__c ?? 'Draft',
            fabricator__c: body.fabricator__c || '',
            kbStudioId: body.kbStudioId || undefined,
        }

        // Parte JSON
        formData.append(
            'sale',
            new Blob([JSON.stringify(salePayload)], { type: 'application/json' })
            // JSON.stringify(salePayload)
        )

        // Adjuntos
        if (Array.isArray(body.attachments)) {
            const currentDate = new Date();
            const timestamp = currentDate.getTime();

            body.attachments.filter((f: File) => !!f).forEach((file: File) => formData.append('attachments', file, 'sales_invoice/' + timestamp + '-' + file.name))
        }

        return this.http.post(url, formData).pipe(
            tap((r) => console.log('Respuesta del backend:', r)),
            catchError((e) => {
                console.error('Error en la solicitud:', e)
                throw e
            })
        )
    }

    updateSale(id: string, body: UpdateSaleBody): Observable<any> {
        const url = `${environment.API_URL}/sales/${id}`

        const salePayload: any = {
            fabricator__c: body.fabricator__c || undefined,
            contactId: body.teamMember || undefined,
            invoiceDate: body.invoiceDate || undefined,
            invoiceNumber: body.invoiceNumber || undefined,
            //teamMember: body.teamMember || undefined,
            status__c: body.status__c || undefined,

            warrantyCustomerAddress: body.warrantyCustomerAddress || undefined,
            warrantyCustomerCity: body.warrantyCustomerCity || undefined,
            warrantyCustomerCountry: body.warrantyCustomerCountry || undefined,
            warrantyCustomerEmail: body.warrantyCustomerEmail || undefined,
            warrantyCustomerLastName: body.warrantyCustomerLastName || undefined,
            warrantyCustomerName: body.warrantyCustomerName || undefined,
            warrantyCustomerPhone: body.warrantyCustomerPhone || undefined,
            warrantyCustomerState: body.warrantyCustomerState || undefined,
            warrantyCustomerZIPCode: body.warrantyCustomerZIPCode || undefined,

            attachmentUrlsToKeep: body.attachmentUrlsToKeep || [],

            saleItems: (body.saleItems || []).map((i: SaleItemInput) => ({
                id: i.id,
                applicationTypeLabel: i.applicationTypeLabel,
                brand: i.brand,
                productCode: i.productCode,
                quantity: i.quantity,
                sku: i.sku,
            })),

            registerWarranty: body.registerWarranty || undefined,
            sendWarrantyInstructions: body.sendWarrantyInstructions || undefined,
            kbStudioId: body.kbStudioId || undefined,
        }

        Object.keys(salePayload).forEach((k) => {
            if (k === 'saleItems' || k === 'attachmentUrlsToKeep') return
            const v = salePayload[k]
            if (v === undefined || v === null || v === '') delete salePayload[k]
        })

        const formData = new FormData()
        formData.append('sale', new Blob([JSON.stringify(salePayload)], { type: 'application/json' }))

        if (Array.isArray(body.attachments)) {
            const currentDate = new Date();
            const timestamp = currentDate.getTime();

            body.attachments.filter((f: File) => !!f).forEach((file: File) => formData.append('attachments', file, 'sales_invoice/' + timestamp + '-' + file.name))
        }

        return this.http.put(url, formData).pipe(
            tap((r) => console.log('PUT /sales/{id} ok:', r)),
            catchError((e) => {
                console.error('PUT /sales/{id} error:', e)
                throw e
            })
        )
    }

    editSampleAlias(sampleId: string, newAlias: string) {
        return this.http.patch<any>(environment.API_URL + '/samples/' + sampleId + '/alias/' + encodeURIComponent(newAlias), {})
    }
    signContract(contract: ContractType) {
        return this.http.post<any>(environment.API_URL + '/sign-contract/' + contract, {})
    }

    getBanners(country: string, userType: string, language: string, loyaltyLevel: string, sapCenterId: string): Observable<any> {
        return this.http.get<any>(environment.API_URL + '/banners', {
            params: {
                country,
                userType,
                language,
                loyaltyLevel,
                sapCenterId,
            },
        })
    }

    getRewardsTc(): Observable<{ url: string }> {
        return this.http.get<{ url: string }>(environment.API_URL + '/rewards-tc')
    }

    getLoyaltyTc(): Observable<{ url: string }> {
        return this.http.get<{ url: string }>(environment.API_URL + '/loyalty/tc')
    }

    getClaimsMinDoc(country: string, language?: string): Observable<{ url: string }> {
        const params: any = { country }
        if (language) {
            params['language'] = language
        }
        return this.http.get<{ url: string }>(environment.API_URL + '/claims-min-docs', { params })
    }

    downloadWarrantyInstructions(saleId: string): Observable<Blob> {
        return this.http.get(`${environment.API_URL}/sales/${saleId}/warranty-instructions`, {
            responseType: 'blob' as 'blob',
        })
    }

    validatePostalCode(postalCode: string, countryCode: string): Observable<{ valid: boolean }> {
        return this.http.post<{ valid: boolean }>(`${environment.API_URL}/partners/validate-postal`, { postalCode, countryCode })
    }

    searchPartners(partnerType: string, postalCode: string, user: UserInfo): Observable<PartnerAccount[]> {
        return this.http.post<PartnerAccount[]>(`${environment.API_URL}/partners/search`, {
            partnerType,
            postalCode,
            user,
        })
    }

    getContractInfo(contractId: ContractType) {
        return this.http.get<SignableContract>(`${environment.API_URL}/sign-contract/${contractId}`)
    }

    createSalesSupport(payload: any): Observable<void> {
        return this.http.post<void>(environment.API_URL + '/sales-support', payload)
    }

    createOnlineVisit(payload: OnlineVisitRequestDTO): Observable<OnlineVisitResponseDTO> {
        return this.http.post<OnlineVisitResponseDTO>(environment.API_URL + '/online-visit', payload)
    }

    getOnlineVisitCooldown(): Observable<OnlineVisitCooldownDTO> {
        return this.http.get<OnlineVisitCooldownDTO>(environment.API_URL + '/online-visit/cooldown')
    }

    getOnlineVisitCurrent(): Observable<OnlineVisitResponseDTO | null> {
        return this.http.get<OnlineVisitResponseDTO | null>(environment.API_URL + '/online-visit/current')
    }

    updateLanguage(language: string) {
        return this.http.patch<any>(environment.API_URL + '/user/language', { language })
    }

    getLeadStats() {
        return this.http.get<any>(environment.API_URL + '/user/lead-stats')
    }

    getLoyaltyNoSalesPopupCount(): Observable<{ count: number }> {
        return this.http.get<{ count: number }>(environment.API_URL + '/user/loyalty-no-sales-popup-count')
    }

    incrementLoyaltyNoSalesPopupCount(): Observable<{ count: number }> {
        return this.http.post<{ count: number }>(environment.API_URL + '/user/loyalty-no-sales-popup-count', {})
    }
    
    updateAccountAllowAccess(allowAccess: boolean) {
        return this.http.patch<any>(environment.API_URL + '/user/allow-impersonate', { allowAccess })
    }

    getCountryPicklist() {
        return this.http.get<PicklistValueDTO[]>(environment.API_URL + '/user/picklist/countries')
    }

    getStatePicklist(countryCode: string) {
        return this.http.get<PicklistValueDTO[]>(environment.API_URL + '/user/picklist/states?countryCode=' + countryCode)
    }

    getAccountSocialInfo(): Observable<AccountSocialInfo> {
        return this.http.get<AccountSocialInfo>(environment.API_URL + '/user/account/social')
    }

    getEmployees(): Observable<EmployeeDto[]> {
        return this.http.get<EmployeeDto[]>(`${environment.API_URL}/user/employees`)
    }

    getShopEmployees(shopId: string): Observable<EmployeeDto[]> {
        return this.http.get<EmployeeDto[]>(`${environment.API_URL}/user/shops/${shopId}/employees`)
    }

    getAssociateShops(): Observable<AssociateShopDto[]> {
        return this.http.get<AssociateShopDto[]>(`${environment.API_URL}/user/associate-shops`)
    }

    updateEmployees(data: EmployeeUpdateRequest[]): Observable<EmployeeDto[]> {
        return this.http.patch<EmployeeDto[]>(`${environment.API_URL}/user/employees`, data)
    }

    saveEmployee(data: EmployeeUpdateRequest): Observable<EmployeeDto> {
        return this.http.patch<EmployeeDto>(`${environment.API_URL}/user/employee`, data)
    }

    deleteEmployee(id: string): Observable<void> {
        return this.http.delete<void>(`${environment.API_URL}/user/employee/${id}`)
    }

    updateShopEmployees(shopId: string, data: EmployeeUpdateRequest[]): Observable<EmployeeDto[]> {
        return this.http.patch<EmployeeDto[]>(`${environment.API_URL}/user/shops/${shopId}/employees`, data)
    }

    saveShopEmployee(shopId: string, data: EmployeeUpdateRequest): Observable<EmployeeDto> {
        return this.http.patch<EmployeeDto>(`${environment.API_URL}/user/shops/${shopId}/employee`, data)
    }

    deleteShopEmployee(shopId: string, id: string): Observable<void> {
        return this.http.delete<void>(`${environment.API_URL}/user/shops/${shopId}/employee/${id}`)
    }

    activateEmployee(id: string): Observable<ActivationResult> {
        return this.http.post<ActivationResult>(`${environment.API_URL}/user/employee/${id}/activate`, {})
    }

    enableActiosMode(allowAccess: boolean): Observable<void> {
        return this.http.post<void>(`${environment.API_URL}/user/account/enable-actios-mode`, { allowAccess })
    }

    updateAccountWeAccess(allowAccess: boolean) {
        return this.http.patch<any>(environment.API_URL + '/user/we-access', { allowAccess })
    }
    updateAccountEcosentinoAccess(allowAccess: boolean) {
        return this.http.patch<any>(environment.API_URL + '/user/ecosentino-access', { allowAccess })
    }
    updateAccountFinancialPortalAccess(allowAccess: boolean) {
        return this.http.patch<any>(environment.API_URL + '/user/financial-portal-access', { allowAccess })
    }
    updateAccountNexusAccess(allowAccess: boolean) {
        return this.http.patch<any>(environment.API_URL + '/user/nexus-access', { allowAccess })
    }
    updateAccountNotificationsFromLeads(allowAccess: boolean) {
        return this.http.patch<any>(environment.API_URL + '/user/notifications-from-leads', { allowAccess })
    }
    updateUserProfileInfo(data: AccountUpdateRequest) {
        return this.http.patch<boolean>(environment.API_URL + '/user/info', data)
    }
    updateAccountInfo(data: AccountUpdateRequest) {
        return this.http.patch<boolean>(environment.API_URL + '/user/account', data)
    }

    updateAccountSocialInfo(data: AccountSocialUpdateRequest) {
        return this.http.patch<boolean>(environment.API_URL + '/user/account/social', data)
    }

    getAccountSelectedServices(): Observable<{ services: string }> {
        return this.http.get<{ services: string }>(`${environment.API_URL}/user/account/selected-services`)
    }

    updateAccountSelectedServices(data: string) {
        return this.http.patch<boolean>(environment.API_URL + '/user/account/selected-services', data)
    }

    unsubscribeFromLeads(data: { reason: string; startDate: string; endDate?: string; comment?: string }) {
        return this.http.patch<boolean>(environment.API_URL + '/user/account/unsubscribe', data)
    }

    getUnsubscribeFromLeadsReason(): Observable<{ reason: string }> {
        return this.http.get<{ reason: string }>(`${environment.API_URL}/user/account/unsubscribe-reason`)
    }

    getChildAccounts() {
        return this.http.get<ChildAccountInfo[]>(environment.API_URL + '/sales/child-accounts')
    }
    // --- INIT myDisplays API calls ---
    myDisplays__createDisplay(newDisplay: any) {
        return this.http.post<{ displayId: string }>(environment.API_URL + '/api/displays', newDisplay)
    }

    myDisplays__getDisplay(displayId: string) {
        return this.http.get<any[]>(environment.API_URL + '/api/displays/' + displayId)
    }

    myDisplays__getDisplayByBrand(brand: string) {
        const params = new HttpParams().set('brand', brand)
        return this.http.get<any[]>(environment.API_URL + '/api/displays/by-brand', { params })
    }

    myDisplays__deleteDisplay(displayId: string) {
        return this.http.delete<void>(environment.API_URL + '/api/displays/' + displayId)
    }

    myDisplays__requestDeletion(displayId: string) {
        return this.http.post<void>(environment.API_URL + `/api/displays/${displayId}/request-deletion`,{});
    }

    myDisplays__cancelRequestDeletion(displayId: string) {
        return this.http.post<void>(environment.API_URL + `/api/displays/${displayId}/cancel-request-deletion`,{});
    }

    myDisplays__requestRenewal(displayId: string, displayToRenewId: string) {
      return this.http.post<void>(environment.API_URL + `/api/displays/request-renewal`,{ displayId: displayId, displayToRenewId: displayToRenewId });
    }

    myDisplays__uploadDocument(body: any) {
        return this.http.post(environment.API_URL + '/api/displays/document', body)
    }

    myDisplays__createTask(body: any) {
        return this.http.post(environment.API_URL + '/api/displays/task', body)
    }

    myDisplays__getKitchenColors(type: string) {
        let params = new HttpParams().set('type', this.normalizeDisplayCatalogType(type))
        return this.http.get<KitchenColor[]>(environment.API_URL + '/api/displays/kitchen-colors', { params: this.addPlatformParam(params) })
    }

    myDisplays__getFinishesByColor(type: string, color: string, brand: string) {
        let params = new HttpParams().set('type', this.normalizeDisplayCatalogType(type)).set('color', color).set('brand', brand)
        return this.http.get<any[]>(environment.API_URL + '/api/displays/finishes', { params: this.addPlatformParam(params) })
    }

    myDisplays__getFormatsByColorAndFinish(type: string, color: string, brand: string, finish: string) {
        let params = new HttpParams().set('type', this.normalizeDisplayCatalogType(type)).set('color', color).set('brand', brand).set('finish', finish)
        return this.http.get<any[]>(environment.API_URL + '/api/displays/formats', { params: this.addPlatformParam(params) })
    }

    myDisplays__getThicknessesByColorFinishAndFormat(type: string, color: string, brand: string, finish: string, format: string) {
        let params = new HttpParams().set('type', this.normalizeDisplayCatalogType(type)).set('color', color).set('brand', brand).set('finish', finish).set('format', format)
        return this.http.get<any[]>(environment.API_URL + '/api/displays/thicknesses', { params: this.addPlatformParam(params) })
    }

    myDisplays__getAddonsByColorFinishAndFormatAndThicknesses(type: string, color: string, brand: string, finish: string, format: string, thickness: string) {
        let params = new HttpParams().set('type', this.normalizeDisplayCatalogType(type)).set('color', color).set('brand', brand).set('finish', finish).set('format', format).set('thickness', thickness)
        return this.http.get<any[]>(environment.API_URL + '/api/displays/addons', { params: this.addPlatformParam(params) })
    }

    myDisplays__getTexturesByColorFinishAndFormatAndThicknesses(type: string, color: string, brand: string, finish: string, format: string, thickness: string) {
      let params = new HttpParams().set('type', this.normalizeDisplayCatalogType(type)).set('color', color).set('brand', brand).set('finish', finish).set('format', format).set('thickness', thickness)
      return this.http.get<any[]>(environment.API_URL + '/api/displays/textures', { params: this.addPlatformParam(params) })
    }

    myDisplays__getSinkModels(application: string) {
        let params = new HttpParams().set('application', application)
        return this.http.get<any[]>(environment.API_URL + '/api/displays/sink-models', { params: this.addPlatformParam(params) })
    }

    myDisplays__getSinkSubModels(model: string) {
        let params = new HttpParams().set('model', model)
        return this.http.get<any[]>(environment.API_URL + '/api/displays/sink-sub-models', { params: this.addPlatformParam(params) })
    }

    myDisplays__getSinkBrands(application: string, model: string, submodel: string) {
        let params = new HttpParams().set('application', application).set('model', model).set('submodel', submodel)
        return this.http.get<any[]>(environment.API_URL + '/api/displays/sink-brands', { params: this.addPlatformParam(params) })
    }

    myDisplays__getSinkColors(application: string, type: string, model: string, submodel: string) {
        let params = new HttpParams().set('application', application).set('type', type).set('model', model).set('submodel', submodel)
        return this.http.get<any[]>(environment.API_URL + '/api/displays/sink-colors', { params: this.addPlatformParam(params) })
    }

    myDisplays__getSinkFinishes(application: string, type: string, brand: string, color: string, model: string, submodel: string) {
        let params = new HttpParams().set('application', application).set('type', type).set('brand', brand).set('color', color).set('model', model).set('submodel', submodel)
        return this.http.get<any[]>(environment.API_URL + '/api/displays/sink-finishes', { params: this.addPlatformParam(params) })
    }

    myDisplays__getSinkFormats(application: string, type: string, brand: string, color: string, finish: string, model: string, submodel: string) {
        let params = new HttpParams().set('application', application).set('type', type).set('brand', brand).set('color', color).set('finish', finish).set('model', model).set('submodel', submodel)
        return this.http.get<any[]>(environment.API_URL + '/api/displays/sink-formats', { params: this.addPlatformParam(params) })
    }

    myDisplays__getModelsCTS(type: string) {
        let params = new HttpParams().set('type', this.normalizeDisplayCatalogType(type))
        return this.http.get<any[]>(environment.API_URL + '/api/displays/models-cts', { params: this.addPlatformParam(params) })
    }

    myDisplays__getFullConfigCTS(type: string, model: string) {
        let params = new HttpParams().set('type', this.normalizeDisplayCatalogType(type)).set('model', model)
        return this.http.get<any[]>(environment.API_URL + '/api/displays/fullconfig-cts', { params: this.addPlatformParam(params) })
    }

    myDisplays__getLegalNoticeLink() {
        const params = new HttpParams()
        return this.http.get<{ link: string }>(environment.API_URL + '/api/displays/legal-notice-link', { params })
    }

    myDisplays__relatedAccounts(includeMyself: boolean = true) {
        const params = new HttpParams().set('includeMyself', includeMyself)
        return this.http.get<LabelValuePair<string>[]>(environment.API_URL + '/api/displays/accounts', {params})
    }
    // --- END myDisplays API calls ---

    // --- INIT Common ---
    getAssociatedStores() {
        return this.http.get<LabelValuePair<string>[]>(environment.API_URL + '/api/displays/associated-stores')
    }
    // --- END Common ---

    validateFiscalId(fiscalId: string): Observable<{ valid: boolean }> {
        return this.http.post<{ valid: boolean }>(`${environment.API_URL}/suppliers/validate-fiscal-id`, fiscalId)
    }

    updateFiscalId(fiscalId: string): Observable<{ url: string }> {
        return this.http.post<{ url: string }>(`${environment.API_URL}/suppliers/update-fiscal-id`, fiscalId)
    }

    getRewardsProviderUrl() {
        return this.http.get<{ redirectUrl: string }>(`${environment.API_URL}/points-provider/redirect-url`)
    }

    getExternalLinkToken(externalSite: string) {
        return this.http.get<{ token: string }>(`${environment.API_URL}/external-token/${externalSite}`)
    }
    getRequestedCommissions() {
        return this.http.get<any>(environment.API_URL + '/commissions/requested-commissions')
    }
    getPendingCommissions(page: number = 0, size: number = 10) {
        return this.http.get<any>(environment.API_URL + '/commissions/pending-commissions')
    }
    requestCommissions(pendingCommissionsId: String[], amount: String, currencyCode: String) {
        return this.http.post<any>(environment.API_URL + '/commissions/request', { pendingCommissionsId, amount, currencyCode })
    }

    getNaturalStoneColorList(centerId: string) {
        const params = new HttpParams()
            .set('centerId', centerId)
        return this.http.get<NaturalStoneColor[]>(
            `${environment.API_URL}/api/natural-stone/color-list`,
            { params: this.addPlatformParam(params) }
        )
    }

    getNaturalStoneLotList(centerId: string, sku: string, withColor = false){
        let params = new HttpParams()
            .set('centerId', centerId)
            .set('sku', sku)

        if (withColor) {
            params = params.set('withPcm', 'true')
        }

        return this.http.get<NaturalStoneLots>(
            `${environment.API_URL}/api/natural-stone/lots-for-sku`,
            { params: this.addPlatformParam(params) }
        )
    }

    confirmNaturalStoneSelection(body: NaturalStoneLotParsed[]){
        return this.http.post<boolean>(`${environment.API_URL}/api/natural-stone/confirm-selection`, body)
    }

    private normalizeDisplayCatalogType(type: string): string {
        return type === 'backsplash' ? 'cover' : type
    }

    /**
     * Método helper para agregar el parámetro `platform` a los HttpParams
     * Esto se usa en todas las llamadas al microservicio de catálogo
     * para filtrar SKUs según la plataforma desde la que se hace la petición
     */
    private addPlatformParam(params?: HttpParams): HttpParams {
        let result = params || new HttpParams()
        const platform = this.platformService.getPlatform()
        return result.set('platform', platform)
    }
}
